import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { RefreshCw, Loader2, X } from 'lucide-react'
import { applyAppUpdate, dismissAppUpdate, useAppUpdateStore } from '../services/appUpdateService'

export default function AppUpdateBanner() {
  const { t } = useTranslation()
  const { buildId, dismissed, recovery, blocked, applying, error } = useAppUpdateStore()
  const updateButton = useRef(null)
  useEffect(() => {
    if (!applying) return undefined
    const button = updateButton.current
    const preventInput = (event) => { event.preventDefault(); event.stopImmediatePropagation() }
    document.addEventListener('keydown', preventInput, true)
    return () => { document.removeEventListener('keydown', preventInput, true); button?.focus() }
  }, [applying])
  if (!applying && !recovery && (!buildId || buildId === dismissed)) return null
  const notice = blocked ? 'busy' : error || (recovery ? 'recovery' : null)

  return (
    <aside className="sticky top-0 z-50 border-b border-accent-primary/20 bg-dark-surface px-14 py-2.5" aria-label={t('appUpdate.available')}>
      <div className="mx-auto flex max-w-screen-xl items-center justify-center gap-3">
        <div className="min-w-0 text-center" role="status" aria-live="polite">
          <p className="text-sm font-semibold text-text-primary">{t('appUpdate.available')}</p>
          {notice && <p className="mt-0.5 text-xs text-text-secondary">{t(`appUpdate.${notice}`)}</p>}
        </div>
        <button ref={updateButton} type="button" disabled={blocked || applying} onClick={applyAppUpdate} className="flex min-h-10 shrink-0 items-center gap-2 rounded-lg bg-accent-primary px-3 text-sm font-bold text-dark-primary transition-opacity disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary">
          <RefreshCw className={`h-4 w-4 shrink-0${applying ? ' animate-spin' : ''}`} aria-hidden="true" />
          {t(applying ? 'appUpdate.applying' : 'appUpdate.refresh')}
        </button>
      </div>
      {!recovery && <button type="button" disabled={applying} onClick={dismissAppUpdate} aria-label={t('common.close')} className="absolute end-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-text-secondary hover:text-white disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary"><X className="h-4 w-4" aria-hidden="true" /></button>}
      {applying && createPortal(<div ref={(node) => node?.focus()} tabIndex={-1} className="fixed inset-0 z-[1000] flex items-center justify-center bg-dark-primary/90 outline-none" role="alertdialog" aria-modal="true" aria-label={t('appUpdate.applying')}>
        <div className="flex items-center gap-3 text-sm text-text-primary"><Loader2 className="h-5 w-5 animate-spin text-accent-primary" aria-hidden="true" />{t('appUpdate.applying')}</div>
      </div>, document.body)}
    </aside>
  )
}
